import React, { useCallback, useEffect, useMemo } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { useTranslation } from 'react-i18next'
import { actionTypes } from 'consts/automatedActionConsts'
import TextAreaAutosize from 'components/common/TextareaAutosize'
import ErrorMessage from 'components/common/text/ErrorMessage'
import SelectInput from 'components/common/SelectInput'
import { useSelector } from 'react-redux'

const useStyles = props =>
  makeStyles({
    messageInput: {
      border: 'none!important'
    },
    messageInputContainer: {
      borderRadius: 10,
      border: ({ errorMessage }) => `1px solid ${errorMessage ? 'red' : 'var(--bg-color-65)'}`,
      paddingLeft: 10
    },
    selectSenderContainer: {
      marginBottom: 20
    }
  })(props)

const SendMessage = ({ onChange, params, errors }) => {
  const errorMessage = useMemo(() => errors.actions?.[actionTypes.SendMessage], [errors])
  const classes = useStyles({ errorMessage })
  const { t } = useTranslation()

  const accountOwnerId = useSelector(state => state.practiceReducer.accountOwner.id)
  const { data: practiceMembers } = useSelector(state => state.profileReducer.practiceMembers)

  const senderOptions = useMemo(
    () =>
      practiceMembers
        .filter(member => member.status !== 'invited' && member.status !== 'disabled')
        .map(member => ({
          value: member.id,
          label: `${member.name}${member.roleDescription ? ` (${member.roleDescription})` : ''}`
        })),
    [practiceMembers]
  )

  const handleParamsChange = useCallback(
    ({ message, executionHour, senderDoctorId }) => {
      onChange({
        type: actionTypes.SendMessage,
        params: {
          message: message ?? (params?.message || ''),
          executionHour: executionHour ?? (params?.executionHour || ''),
          senderDoctorId: senderDoctorId ?? (params?.senderDoctorId || '')
        }
      })
    },
    [onChange, params]
  )

  useEffect(() => {
    if (!params?.senderDoctorId) {
      handleParamsChange({ senderDoctorId: accountOwnerId })
    }
  }, [accountOwnerId, handleParamsChange, params])

  return (
    <Grid container direction="column">
      <Grid item>
        <SelectInput
          style={{ bright: true, thick: true }}
          options={senderOptions}
          value={params?.senderDoctorId || accountOwnerId}
          onChange={option => handleParamsChange({ senderDoctorId: option.value })}
          label={t('pages.accountSettings.automations.editorModal.selectSender')}
          keepErrorContainerWhenInactive={false}
          containerClassName={classes.selectSenderContainer}
        />
      </Grid>
      <Grid item>
        <DazzedParagraph14 strong>
          {t('pages.accountSettings.automations.editorModal.sendMessageLabel')}
        </DazzedParagraph14>
      </Grid>
      {/* To hide*/}
      <Grid item>
        <DazzedParagraph12>
          {t('pages.accountSettings.automations.editorModal.sendMessagePlaceholdersHint')}
        </DazzedParagraph12>
      </Grid>
      <Grid item xs={12} className={classes.messageInputContainer}>
        <TextAreaAutosize
          inputStyle={{ fontSize: 14, fontWeight: 500, height: 100, overflow: 'auto' }}
          rowsMax={4}
          style={{ bright: true }}
          className={classes.messageInput}
          placeholder={t('pages.accountSettings.automations.editorModal.sendMessagePlaceholder')}
          keepErrorContainerWhenInactive={false}
          value={params?.message || ''}
          setValue={message => handleParamsChange({ message })}
        />
      </Grid>
      {errorMessage && (
        <Grid item>
          <ErrorMessage active={!!errorMessage} text={errorMessage} />
        </Grid>
      )}
    </Grid>
  )
}
export default SendMessage
